import { doc, updateDoc, increment, getDoc, setDoc } from 'firebase/firestore'
import { db, auth } from '@/firebase/config'
import {
  trackBuyingJourney,
  getStoredJourneyPlan
} from '@/services/customerJourneyService'
import {
  JOURNEY_FUNNEL_STAGES,
  JOURNEY_FUNNEL_EVENTS
} from '@/constants/buyingJourney'

const RETENTION_FIRED_THIS_SESSION_KEY = 'digipalz_retention_fired_this_session'

class SessionTracker {
  constructor() {
    this.sessionStartTime = null
    this.currentUserId = null
    this.isTracking = false
    this.beforeUnloadListener = null
    this.pausedDuration = 0
  }

  /**
   * Starts session tracking for a user
   * @param {string} userId - The user's UID
   */
  startSession(userId) {
    if (!userId) {
      return
    }

    const hasSessionBefore =
      typeof window !== 'undefined' &&
      window.localStorage?.getItem('digipalz_has_session_before') === '1'
    const alreadyFiredThisSession =
      typeof window !== 'undefined' &&
      window.sessionStorage?.getItem(RETENTION_FIRED_THIS_SESSION_KEY) === '1'

    if (hasSessionBefore && !alreadyFiredThisSession) {
      try {
        const planType =
          typeof getStoredJourneyPlan === 'function'
            ? getStoredJourneyPlan()
            : null
        trackBuyingJourney({
          stage: JOURNEY_FUNNEL_STAGES.J_RETENTION,
          eventName: JOURNEY_FUNNEL_EVENTS.RETURN_SESSION,
          userId,
          ...(planType && { planType })
        })
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.setItem(RETENTION_FIRED_THIS_SESSION_KEY, '1')
        }
      } catch (e) {
        // ignore
      }
    }

    // End any existing session first
    this.endSession()

    this.currentUserId = userId
    this.sessionStartTime = Date.now()
    this.pausedDuration = 0
    this.isTracking = true

    // Set up beforeunload listener to save session when user leaves
    this.beforeUnloadListener = () => {
      this.endSession()
    }

    window.addEventListener('beforeunload', this.beforeUnloadListener)

    // Listen for visibility changes to pause/resume tracking (WITHOUT Firebase writes)
    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange.bind(this)
    )
  }

  /**
   * Handles visibility change events (tab switching)
   * Only tracks time locally, NO Firebase writes
   */
  handleVisibilityChange() {
    if (document.visibilityState === 'hidden' && this.isTracking) {
      // User switched away from the tab, pause tracking locally
      this.pauseSession()
    } else if (
      document.visibilityState === 'visible' &&
      this.currentUserId &&
      !this.isTracking
    ) {
      // User returned to the tab, resume tracking locally
      this.resumeSession()
    }
  }

  /**
   * Pauses session tracking (when user switches tabs)
   * NO Firebase write - just tracks time locally
   */
  pauseSession() {
    if (!this.isTracking || !this.sessionStartTime) return

    // Calculate time spent before pausing
    const activeTime = Date.now() - this.sessionStartTime
    this.pausedDuration += activeTime

    this.isTracking = false
  }

  /**
   * Resumes session tracking (when user returns to tab)
   * NO Firebase write - just resumes time tracking
   */
  resumeSession() {
    if (this.isTracking || !this.currentUserId) return

    this.sessionStartTime = Date.now()
    this.isTracking = true
  }

  /**
   * Ends the current session and updates total time spent
   * ONLY Firebase write happens here (on logout/page close)
   * Returns a Promise so callers can await the Firestore update before sign-out
   */
  async endSession() {
    if (!this.currentUserId) {
      return
    }

    const userId = this.currentUserId
    const totalDuration =
      this.pausedDuration +
      (this.isTracking && this.sessionStartTime
        ? Date.now() - this.sessionStartTime
        : 0)

    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('digipalz_has_session_before', '1')
      }
    } catch (e) {
      // ignore
    }

    this.cleanup()

    if (totalDuration > 0) {
      await this.updateTotalTimeSpentWithUserId(userId, totalDuration)
    }
  }

  /**
   * Updates the total time spent in Firestore
   * ONLY called on endSession (logout/page close)
   * @param {string} userId - User UID (passed so update works after cleanup())
   * @param {number} duration - Session duration in milliseconds
   */
  async updateTotalTimeSpentWithUserId(userId, duration) {
    if (!userId || !duration || duration < 0) return
    if (!auth.currentUser || auth.currentUser.uid !== userId) return

    try {
      const userDocRef = doc(db, 'users', userId)
      const docSnap = await getDoc(userDocRef)

      if (docSnap.exists()) {
        await updateDoc(userDocRef, {
          totalTimeSpent: increment(duration)
        })
      } else {
        await setDoc(userDocRef, { totalTimeSpent: duration }, { merge: true })
      }
    } catch (error) {
      console.error('Error updating total time spent:', error)
    }
  }

  /**
   * Cleans up session tracking
   */
  cleanup() {
    if (this.beforeUnloadListener) {
      window.removeEventListener('beforeunload', this.beforeUnloadListener)
      this.beforeUnloadListener = null
    }

    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange.bind(this)
    )

    this.sessionStartTime = null
    this.currentUserId = null
    this.isTracking = false
  }

  /**
   * Gets the current session duration
   * @returns {number} Current session duration in milliseconds
   */
  getCurrentSessionDuration() {
    if (!this.isTracking || !this.sessionStartTime) return 0
    return Date.now() - this.sessionStartTime
  }

  /**
   * Checks if session is currently being tracked
   * @returns {boolean} True if session is being tracked
   */
  isSessionActive() {
    return this.isTracking && this.sessionStartTime !== null
  }
}

// Create a singleton instance
const sessionTracker = new SessionTracker()

export default sessionTracker
